// A package's plates made into an MP4 in the user's own browser: the same
// animation code the videos are drawn with on the server (data.stage, run in a
// sandboxed frame), every frame drawn at 1920×1080 as fast as the GPU goes —
// not in real time — and encoded by the browser's video encoder (H.264, high
// bitrate). Seconds instead of minutes: no queue, no server Chrome.
// (A see-through .mov — ProRes with alpha — can't be made in a browser: the server makes those.)
import { Muxer, ArrayBufferTarget } from 'mp4-muxer';

const FPS = 25;
const BITRATE = 16_000_000; // 1080p at 16 Mbit/s: broadcast quality
const CODECS = ['avc1.640028', 'avc1.4d0028', 'avc1.42e028'];

// In its own scope (the animation code declares names of its own).
const DRIVER = String.raw`(() => {
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const timing = (total) => ({ total, intro: clamp(total * 0.14, 0.5, 1.4), enter: clamp(total * 0.09, 0.45, 0.9), exit: clamp(total * 0.08, 0.4, 0.8), loop: false });
// Frames sent and not yet taken by the encoder: up to 4 ahead (drawing and encoding overlap).
let inFlight = 0; let wake = null;
window.addEventListener('message', async (e) => {
  if (e.data && e.data.type === 'ack') { inFlight--; if (wake) { const w = wake; wake = null; w(); } }
  if (!e.data || e.data.type !== 'render') return;
  const { data, fps } = e.data;
  try {
    for (let i = 0; i < data.plates.length; i++) {
      const plate = data.plates[i];
      document.querySelectorAll('canvas').forEach((c) => c.remove());
      await window.__stage.init({
        ...data.spec, live: true, sequence: true, textAnim: plate.anim,
        backdrop: { url: data.background },
        text: plate.words.map(({ src, ...w }) => ({ ...w, url: src })),
      }, { width: data.size[0], height: data.size[1] });
      const canvas = document.querySelector('canvas');
      const seq = timing(plate.seconds);
      const loop = (data.spec.seconds || 10) * 25;
      const frames = Math.round(plate.seconds * fps);
      for (let f = 0; f < frames; f++) {
        const t = f / fps; const f25 = t * 25;
        await window.__stage.frame((f25 % loop) / loop, Math.floor(f25), null, { ...seq, t });
        const bmp = await createImageBitmap(canvas);
        inFlight++;
        parent.postMessage({ type: 'frame', plate: i, bmp }, '*', [bmp]);
        while (inFlight > 4) await new Promise((go) => { wake = go; });
      }
    }
    while (inFlight > 0) await new Promise((go) => { wake = go; });
    parent.postMessage({ type: 'done' }, '*');
  } catch (err) {
    parent.postMessage({ type: 'error', message: String(err && err.message || err) }, '*');
  }
});
parent.postMessage({ type: 'ready' }, '*');
})();`;

let supported = null;
/** The encoder config this browser can use for 1080p H.264 (hardware first), or null. */
async function encoderConfig(width, height) {
  if (typeof window.VideoEncoder !== 'function' || typeof window.VideoFrame !== 'function') return null;
  for (const codec of CODECS) {
    for (const hardwareAcceleration of ['prefer-hardware', 'no-preference']) {
      const config = { codec, width, height, bitrate: BITRATE, framerate: FPS, hardwareAcceleration, avc: { format: 'avc' } };
      // eslint-disable-next-line no-await-in-loop
      const ok = await window.VideoEncoder.isConfigSupported(config).then((r) => r.supported).catch(() => false);
      if (ok) return config;
    }
  }
  return null;
}
/** Whether this browser can make the videos itself (else the server does). */
export async function canMakeVideos() {
  if (supported === null) supported = Boolean(await encoderConfig(1920, 1080));
  return supported;
}

/**
 * The plates in `data` (api.platePackageLive with full: true) as one MP4
 * (Blob), one after another. onProgress(done, total) as frames are made.
 */
export async function makeVideo(data, { onProgress } = {}) {
  const [width, height] = data.size;
  const config = await encoderConfig(width, height);
  if (!config) throw new Error('This browser cannot make videos itself.');
  const total = data.plates.reduce((n, p) => n + Math.round(p.seconds * FPS), 0);
  const muxer = new Muxer({ target: new ArrayBufferTarget(), video: { codec: 'avc', width, height, frameRate: FPS }, fastStart: 'in-memory' });
  let failed = null;
  const encoder = new window.VideoEncoder({
    output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
    error: (e) => { failed = e; },
  });
  encoder.configure(config);

  const frame = document.createElement('iframe');
  frame.setAttribute('sandbox', 'allow-scripts');
  frame.style.cssText = `position:fixed;left:-${width + 100}px;top:0;width:${width}px;height:${height}px;border:0;opacity:0;pointer-events:none`;
  const safe = (code) => code.replace(/<\/script/gi, '<\\/script');
  frame.srcdoc = `<!doctype html><html><head><meta charset="utf-8"><style>html,body{margin:0;background:transparent}</style></head><body><script>${safe(data.stage)}</script><script>${safe(DRIVER)}</script></body></html>`;
  try {
    await new Promise((resolve, reject) => {
      let n = 0;
      const onMessage = async (e) => {
        if (e.source !== frame.contentWindow) return;
        const m = e.data || {};
        if (m.type === 'ready') frame.contentWindow.postMessage({ type: 'render', data, fps: FPS }, '*');
        else if (m.type === 'frame') {
          const vf = new window.VideoFrame(m.bmp, { timestamp: Math.round((n * 1e6) / FPS), duration: Math.round(1e6 / FPS) });
          encoder.encode(vf, { keyFrame: n % (FPS * 2) === 0 });
          vf.close(); m.bmp.close();
          n++;
          onProgress?.(n, total);
          // Never far ahead of the encoder (memory stays small).
          while (encoder.encodeQueueSize > 6 && !failed) await new Promise((r) => setTimeout(r, 2));
          if (failed) { cleanup(); reject(failed); return; }
          frame.contentWindow.postMessage({ type: 'ack' }, '*');
        } else if (m.type === 'done') { cleanup(); resolve(); } else if (m.type === 'error') { cleanup(); reject(new Error(m.message)); }
      };
      const cleanup = () => window.removeEventListener('message', onMessage);
      window.addEventListener('message', onMessage);
      document.body.appendChild(frame);
    });
    await encoder.flush();
    if (failed) throw failed;
    muxer.finalize();
    return new Blob([muxer.target.buffer], { type: 'video/mp4' });
  } finally {
    if (encoder.state !== 'closed') encoder.close();
    frame.remove();
  }
}

/** Save a Blob as a file. */
export function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement('a'), { href: url, download: name });
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}
